'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AyakTerlemesiIcinBotoksTedavisiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/ayak-terlemesi-icin-botoks-tedavisi';
  const pageTitle = 'Ayak Terlemesi İçin Botoks Tedavisi';
  const pageDescription = 'Aşırı ayak terlemesi (plantar hiperhidroz) botoks ile tedavi edilebilir mi? Etki süresi, avantajları ve uygulama süreci hakkında tüm detaylar burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Ayak Terlemesi Botoks Tedavisi', href: '/tr/blog/ayak-terlemesi-icin-botoks-tedavisi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Ayak terlemesi botoks tedavisi ve hiperhidroz çözümü değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Terleme Tedavisi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ayak Terlemesi İçin Botoks Tedavisi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>31 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Aşırı ayak terlemesi, tıp dilinde plantar hiperhidroz olarak adlandırılır. Bu durum, yalnızca sosyal bir rahatsızlık değil, aynı zamanda yaşam kalitesini ciddi şekilde etkileyen bir problemdir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoks (botulinum toksin), ter bezlerinin aşırı çalışmasını geçici olarak durdurarak terlemeyi büyük oranda azaltır ve kişinin günlük konforunu artırır. Ayak terlemesi için botoks tedavisi, cerrahi gerektirmeyen, etkili ve güvenli bir yöntemdir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Ayak Terlemesi Neden Olur?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ayak tabanındaki ter bezleri, bazı kişilerde genetik veya nörolojik nedenlerle aşırı uyarılır. Bu durum özellikle:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Stres ve heyecan durumlarında</li>
                  <li>Kapalı ayakkabıların uzun süre giyilmesinde</li>
                  <li>Hormonal değişikliklerde artış gösterir</li>
                </ul>
                <p className="text-blue-800 mt-3 font-medium">
                  Botoks, bu bezlerin sinir uyarılarını bloke ederek aşırı terlemeyi durdurur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoks Tedavisi Nasıl Etki Eder?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, asetilkolin adlı nörotransmiteri geçici olarak bloke eder. Bu sayede ter bezlerine "ter üret" sinyali ulaşmaz ve terleme azalır.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium">
                  💡 Etki sadece ter bezlerinde görülür — vücudun ısı dengesi veya diğer terleme alanları etkilenmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ayak botoksu, deneyimli hekimler tarafından klinik ortamda uygulanır:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-blue-800 space-y-2">
                  <li>Ayak tabanına lokal anestezik krem veya soğuk uygulama yapılır</li>
                  <li>Terleme alanları belirlenir (nişasta-iyot testi ile)</li>
                  <li>Her iki ayağa ortalama 40–50 küçük enjeksiyon noktası uygulanır</li>
                  <li>İşlem süresi yaklaşık 20–30 dakika sürer</li>
                </ol>
                <p className="text-blue-800 mt-3 font-medium">
                  🕒 Etki genellikle 3–5 gün içinde başlar, maksimum etki 2 haftada ortaya çıkar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etki Süresi ve Kalıcılık
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun etkisi ortalama 6–9 ay sürer. Bu süre kişisel faktörlere göre değişebilir:
              </p>

              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etkiyi Etkileyen Faktör
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Metabolizma Hızı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hızlı metabolizma etkisini daha erken bitirir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Mevsim / Sıcaklık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yaz aylarında etki süresi daha kısa olabilir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Terleme Şiddeti</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Çok yoğun terlemede doz artırımı gerekebilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Düzenli seanslarla terleme kontrolü uzun süreli hale gelir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Avantajları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Ameliyatsız, hızlı ve güvenli yöntem</li>
                  <li>✅ Günlük yaşama hemen dönüş</li>
                  <li>✅ Koku ve nem azalması</li>
                  <li>✅ Ayakkabı içinde kayma ve rahatsızlık hissinin ortadan kalkması</li>
                  <li>✅ Etkili sonuç (terlemede %80–90 azalma)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Olası Yan Etkiler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Nadir durumlarda:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>Hafif kızarıklık veya iğne batma hissi</li>
                  <li>Geçici ayak tabanı hassasiyeti</li>
                  <li>Nadiren yüzeysel morarma görülebilir</li>
                </ul>
                <p className="text-yellow-800 mt-3 font-medium">
                  Bu etkiler 1–2 gün içinde kendiliğinden geçer. Botoks uygulaması sadece ter bezlerini etkiler, kas gücü veya his üzerinde değişiklik oluşturmaz.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Aşırı terleme nedeniyle sosyal veya iş hayatında rahatsızlık yaşayanlar</li>
                    <li>Medikal kremler veya roll-on tedavilerinden fayda görmeyenler</li>
                    <li>Cerrahi (ETS) işlem istemeyen kişiler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Kimlere uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Gebelik ve emzirme döneminde olanlar</li>
                    <li>Botulinum toksine karşı alerjisi olanlar</li>
                    <li>Enjeksiyon bölgesinde enfeksiyonu bulunanlar</li>
                  </ul>
                </div>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Ayak terlemesi botoks tedavisi ve hiperhidroz çözümü"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ayak botoksu ağrılı mıdır?</h3>
                  <p className="text-gray-700">Hassas bölge olduğu için hafif batma hissi olabilir; genellikle anestezik krem veya buz uygulaması ile konfor sağlanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Ortalama 6–9 ay sürer; düzenli uygulamalarda daha uzun etki sağlanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Yürümeyi etkiler mi?</h3>
                  <p className="text-gray-700">Hayır, kaslar değil sadece ter bezleri etkilenir; yürüyüş fonksiyonları tamamen korunur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Ayak terlemesi tamamen geçer mi?</h3>
                  <p className="text-gray-700">Terleme %80–90 oranında azalır, bazı kişilerde tamamen kaybolur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Botoks başka bölgelerdeki terlemeyi artırır mı?</h3>
                  <p className="text-gray-700">Hayır, yalnızca uygulama yapılan bölgede etki gösterir; vücudun terleme dengesi korunur.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "Botulinum Toxin in Hyperhidrosis Treatment"</li>
                <li>Türk Dermatoloji Derneği – Hiperhidroz (Aşırı Terleme) Rehberi</li>
                <li>Mayo Clinic – "Botox Injections for Excessive Sweating"</li>
                <li>Harvard Health Publishing – "Hyperhidrosis and Treatment Options"</li>
                <li>ISAPS 2024 Global Report – "Non-Surgical Approaches in Sweat Gland Control"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#ayakterlemesi</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#hiperhidroz</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#terlemeterapisi</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#ayakbakımı</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Ayak Terlemesi Botoks Tedavisi İçin Randevu Alın</h3>
              <p className="mb-6">Aşırı terleme sorununuz için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Ayak terlemesi botoks tedavisi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-green-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/ceneye-botoks-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Çeneye Botoks Uygulaması Nedir?</h4>
              <p className="text-gray-600 text-sm">V-line estetik ve bruksizm tedavisi hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoks etki süresi ve kalıcılık hakkında detaylı bilgiler.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
